"use client";

import { c as _c } from "react/compiler-runtime";
import { FileText, ImageIcon, X } from "lucide-react";
import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { formatBytes, IMAGE_KINDS, kindOf, kindsLabel } from "@/lib/files";
import { cn } from "@/lib/utils";

/** The files a tool is working on: thumbnail, name, type, size, optional
 *  details such as pixel size, and a remove button (FR-3). */
export function FileStrip(t0) {
  const $ = _c(13);
  const {
    files,
    details,
    onRemove,
    selected,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== details || $[1] !== files || $[2] !== onRemove || $[3] !== onSelect || $[4] !== selected) {
    let t2;
    if ($[6] !== details || $[7] !== onRemove || $[8] !== onSelect || $[9] !== selected) {
      t2 = (file, i) => {
        const kind = kindOf(file);
        const meta = [kind ? kindsLabel([kind]) : "File", formatBytes(file.size), details?.[i]].filter(Boolean).join(" \xB7 ");
        const isSelected = onSelect && selected === i;
        const body = <><Thumb file={file} /><span className="flex min-w-0 flex-1 flex-col text-left"><span className="truncate font-semibold" title={file.name}>{file.name}</span><span className="text-sm text-muted-foreground">{meta}</span></span></>;
        return <li key={`${file.name}-${file.size}-${file.lastModified}-${i}`} className={cn("flex items-center gap-3 rounded-lg border bg-card py-1.5 pr-1.5 pl-1.5", isSelected && "border-brand outline-2 outline-brand")}>{onSelect ? <button type="button" onClick={() => onSelect(i)} aria-pressed={isSelected} aria-label={`Preview ${file.name}`} className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md">{body}</button> : body}{onRemove && <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${file.name}`} className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-sand hover:text-ink"><X className="size-5" aria-hidden="true" /></button>}</li>;
      };
      $[6] = details;
      $[7] = onRemove;
      $[8] = onSelect;
      $[9] = selected;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = files.map(t2);
    $[0] = details;
    $[1] = files;
    $[2] = onRemove;
    $[3] = onSelect;
    $[4] = selected;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <ul aria-label="Your files" className="flex max-h-[360px] flex-col gap-2 overflow-y-auto">{t1}</ul>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
function Thumb(t0) {
  const $ = _c(11);
  const {
    file
  } = t0;
  const kind = kindOf(file);
  const [broken, setBroken] = useState(false);
  if (kind && IMAGE_KINDS.includes(kind) && !broken) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn("flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md", "bg-sand");
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setBroken(true);
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== file) {
      t3 = <span className={t1}><BlobImage blob={file} alt="" onError={t2} className="size-full object-cover" /></span>;
      $[2] = file;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  const isImage = kind !== null && IMAGE_KINDS.includes(kind);
  const Icon = isImage ? ImageIcon : FileText;
  const t1 = kind === "pdf" ? "bg-pdf-tint text-pdf" : isImage ? "bg-image-tint text-image" : "bg-brand-tint text-brand-strong";
  let t2;
  if ($[4] !== t1) {
    t2 = cn("flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md", t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Icon) {
    t3 = <Icon className="size-5" aria-hidden="true" />;
    $[6] = Icon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}